import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';

/**
 * ProductSearchInput - product picker with server-side search (code, name or customer).
 *
 * Every keystroke stays in the field; after 300 ms without typing it asks the API for the
 * matching products, so any product can be found no matter how many exist (the list shows the
 * first `pageSize` matches). Choosing an option calls `onSelect(product)`. Leaving the field
 * without choosing restores the selected product's label; clearing it calls `onSelect(null)`.
 *
 * Usage:
 *   <ProductSearchInput
 *     value={line.product_id}
 *     selectedLabel={line.product_code ? `${line.product_code} - ${line.product_name}` : ''}
 *     onSelect={(product) => ...}
 *   />
 */
export const productLabel = (p) => (p ? `${p.product_code} - ${p.product_name}` : '');
const productId = (p) => p.product_id ?? p.id;

let instances = 0;

function ProductSearchInput({
  value,
  selectedLabel = '',
  onSelect,
  disabled = false,
  placeholder = null,
  className = 'form-input-inline',
  style = { width: '100%' },
  pageSize = 50,
  id = null,
  ariaLabel = null,
}) {
  const { t } = useTranslation('common');
  const [text, setText] = useState(selectedLabel || '');
  const [options, setOptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [term, setTerm] = useState(null); // null: nothing requested yet
  const [focused, setFocused] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const listId = useRef(`product-search-${++instances}`).current;
  const requestSeq = useRef(0);

  // Show the selected product when it changes from outside (load, another row, reset)
  useEffect(() => {
    if (value) setText(selectedLabel || '');
  }, [value, selectedLabel]);

  // Debounced server search
  useEffect(() => {
    if (term === null) return undefined;
    const timer = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const params = new URLSearchParams({ page_size: String(pageSize) });
        if (term) params.set('search', term);
        const response = await apiFetch(getApiUrl(`/api/products?${params}`));
        if (seq !== requestSeq.current) return; // a newer search is on its way
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const list = result.products || (Array.isArray(result) ? result : []);
        setOptions(list);
        setTotal(result.total ?? list.length);
        setLoadFailed(false);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        console.error('Error searching products:', e);
        setLoadFailed(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [term, pageSize]);

  const findExact = (val) => options.find((p) => productLabel(p) === val);

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);
    const match = findExact(val);
    if (match) {
      onSelect(match);
      return; // picked from the list: keep the current options, no new search
    }
    if (!val) onSelect(null);
    setTerm(val.trim());
  };

  const handleFocus = () => {
    setFocused(true);
    if (term === null) setTerm('');
  };

  const handleBlur = () => {
    setFocused(false);
    if (findExact(text)) return;
    // A typed code that matches exactly one product counts as choosing it
    const typed = text.trim().toLowerCase();
    const byCode = typed ? options.filter((p) => (p.product_code || '').toLowerCase() === typed) : [];
    if (byCode.length === 1) {
      onSelect(byCode[0]);
      setText(productLabel(byCode[0]));
    } else if (value) {
      setText(selectedLabel || '');
    }
  };

  return (
    <div className="product-search-input" style={{ position: 'relative', minWidth: '200px' }}>
      <input
        type="text"
        id={id || undefined}
        aria-label={ariaLabel || t('productSearch.label')}
        list={listId}
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder || t('productSearch.placeholder')}
        className={className}
        style={style}
        disabled={disabled}
        autoComplete="off"
      />
      <datalist id={listId}>
        {options.map((p) => (
          <option key={productId(p)} value={productLabel(p)} />
        ))}
      </datalist>
      {loadFailed && <small className="field-error" role="alert">{t('productSearch.loadFailed')}</small>}
      {!loadFailed && focused && total > options.length && (
        <small className="field-hint">{t('productSearch.moreResults', { shown: options.length, total })}</small>
      )}
    </div>
  );
}

export default ProductSearchInput;
